import { useEffect, useState } from 'react';
import { useLang } from '../../../context/LanguageContext';

const stats = [
  { value: '500+', labelRo: 'Instalări', labelRu: 'Установок' },
  { value: '10+', labelRo: 'Ani Experiență', labelRu: 'Лет опыта' },
  { value: '24/7', labelRo: 'Suport', labelRu: 'Поддержка' },
  { value: '100%', labelRo: 'Garanție', labelRu: 'Гарантия' },
];

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen w-full flex flex-col overflow-hidden">
      {/* Background image — blurred */}
      <div className="absolute inset-0 z-0 scale-105">
        <img
          src="https://public.readdy.ai/ai/img_res/ba63fa19371e7a4035ecc05af8b3d8cb.jpg"
          alt="Security Control Room"
          className="w-full h-full object-cover object-top"
          style={{ filter: 'blur(5px) brightness(0.55)' }}
        />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-charcoal/70 via-charcoal/40 to-charcoal/80"></div>
      <div className="absolute inset-0 z-0 cyber-grid-bg opacity-15"></div>

      {/* Floating glow orbs */}
      <div
        className="absolute top-1/4 left-1/6 w-48 md:w-64 h-48 md:h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.4) 0%, transparent 70%)', animation: 'float 8s ease-in-out infinite' }}
      />
      <div
        className="absolute bottom-1/3 right-1/6 w-36 md:w-48 h-36 md:h-48 rounded-full opacity-8 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.3) 0%, transparent 70%)', animation: 'float 10s ease-in-out infinite reverse' }}
      />

      {/* Main Content — pt clears fixed navbar, flex-1 + justify-center centers the rest */}
      <div
        className={`relative z-10 flex-1 flex flex-col items-center justify-center px-5 sm:px-8 pt-[80px] pb-4 text-center transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-neon/40 bg-neon/5 mb-4 sm:mb-5"
          style={{ transition: 'all 0.6s ease 0.15s', opacity: visible ? 1 : 0 }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse flex-shrink-0"></span>
          <span className="font-inter text-[9px] sm:text-[10px] text-neon tracking-widest uppercase">
            {t('Chișinău, Moldova — Instalare Profesională', 'Кишинёв, Молдова — Профессиональная установка')}
          </span>
        </div>

        {/* Headline — 2 lines, bigger on mobile */}
        <h1
          className="font-rajdhani font-bold leading-tight mb-4 sm:mb-5"
          style={{ transition: 'all 0.7s ease 0.25s', opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)' }}
        >
          <span className="block text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl text-white">
            {t('Alarmă & Supraveghere', 'Сигнализация и Камеры')}
          </span>
          <span className="block text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl text-white">
            {t('Instalate în ', 'Установка за ')}<span className="text-neon" style={{ textShadow: '0 0 20px rgba(0,255,136,0.7), 0 0 40px rgba(0,255,136,0.3)' }}>
              {t('24h', '24ч')}
            </span>
          </span>
        </h1>

        {/* Subline */}
        <p
          className="max-w-md sm:max-w-lg mx-auto font-inter text-sm sm:text-base md:text-lg text-white/85 leading-relaxed mb-7 sm:mb-8"
          style={{ transition: 'all 0.7s ease 0.38s', opacity: visible ? 1 : 0 }}
        >
          {t(
            'Tehnicieni certificați Ajax, Hikvision și Dahua — garanție 3 ani și suport 24/7 în toată Moldova.',
            'Сертифицированные специалисты Ajax, Hikvision и Dahua — гарантия 3 года и поддержка 24/7 по всей Молдове.'
          )}
        </p>

        {/* CTA + compact trust chips */}
        <div
          className="flex flex-col items-center gap-3"
          style={{ transition: 'all 0.7s ease 0.48s', opacity: visible ? 1 : 0 }}
        >
          <a
            href="https://wa.me/37360123456?text=Buna%20ziua!%20Doresc%20un%20deviz%20gratuit."
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2.5 px-5 sm:px-8 md:px-10 py-3.5 sm:py-4 rounded-md bg-neon text-charcoal font-rajdhani font-bold text-sm sm:text-base md:text-xl btn-neon transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: '0 0 30px rgba(0,255,136,0.5), 0 0 60px rgba(0,255,136,0.2)' }}
          >
            <i className="ri-whatsapp-line text-lg sm:text-xl md:text-2xl"></i>
            {t('Cere Deviz Gratuit — WhatsApp', 'Запросить смету — WhatsApp')}
          </a>

          {/* Compact single-row trust chips — won't overflow button */}
          <p className="font-inter text-[11px] sm:text-xs text-white/55 flex items-center gap-1.5 flex-wrap justify-center">
            <span className="flex items-center gap-1"><i className="ri-check-line text-neon text-xs"></i>{t('Gratuit', 'Бесплатно')}</span>
            <span className="text-white/25">·</span>
            <span className="flex items-center gap-1"><i className="ri-check-line text-neon text-xs"></i>{t('Fără obligații', 'Без обязательств')}</span>
            <span className="text-white/25">·</span>
            <span className="flex items-center gap-1"><i className="ri-check-line text-neon text-xs"></i>{t('Răspuns în 30 min', 'Ответ за 30 мин')}</span>
          </p>
        </div>

        {/* Spacer — fixed, never grows */}
        <div className="h-7 sm:h-9 md:h-12"></div>

        {/* Stats — bigger text, clearly readable on mobile */}
        <div
          className="w-full max-w-xl mx-auto grid grid-cols-4 rounded-xl border border-neon/15 bg-charcoal/80 backdrop-blur-sm overflow-hidden mb-8 sm:mb-10 md:mb-14"
          style={{ transition: 'all 0.8s ease 0.65s', opacity: visible ? 1 : 0 }}
        >
          {stats.map((stat, idx) => (
            <div
              key={stat.value}
              className={`py-4 sm:py-5 px-2 text-center ${idx < stats.length - 1 ? 'border-r border-neon/10' : ''}`}
            >
              <div
                className="font-rajdhani font-bold text-2xl sm:text-3xl md:text-3xl text-neon leading-none"
                style={{ textShadow: '0 0 10px rgba(0,255,136,0.5)' }}
              >
                {stat.value}
              </div>
              <div className="font-inter text-[11px] sm:text-xs md:text-sm text-white/80 mt-1 leading-tight">
                {t(stat.labelRo, stat.labelRu)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-charcoal to-transparent z-10 pointer-events-none"></div>
    </section>
  );
}
